import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Database, Download, Loader2, MapPin, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";

const DATA_GOV = "https://data.gov.au/dataset/geocoded-national-address-file-g-naf";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function ImportProgress({ s }) {
  return (
    <div className="rounded-md border border-kilted-gold/60 bg-kilted-gold/10 p-3 text-sm" data-testid="gnaf-progress">
      <div className="flex items-center gap-2 font-medium"><Loader2 className="h-4 w-4 animate-spin" /> {s.stage || "Working…"}</div>
      <div className="mt-1 text-xs text-muted-foreground">
        {s.downloaded_mb ? `${s.downloaded_mb} MB downloaded · ` : ""}{(s.rows_scanned || 0).toLocaleString()} rows scanned · {(s.kept || 0).toLocaleString()} in-region · {(s.inserted || 0).toLocaleString()} written
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Started {fmt(s.started_at)}. You can leave this page — the import keeps running on the server.</p>
    </div>
  );
}

const PROVIDERS = ["G-NAF", "Google", "AddressFinder", "Nominatim", "none"];

function LookupStats({ stats }) {
  const total = Object.values(stats || {}).reduce((a, b) => a + b, 0);
  if (!total) return <p className="text-xs text-muted-foreground" data-testid="lookup-stats-empty">No lookups recorded in the last 7 days yet.</p>;
  return (
    <div className="flex flex-wrap gap-2" data-testid="lookup-stats">
      {PROVIDERS.filter((p) => stats[p]).map((p) => (
        <Badge key={p} variant="outline" data-testid={`lookup-stat-${p.toLowerCase()}`} className={p === "Nominatim" ? "border-amber-400 text-amber-700" : ""}>
          {p === "none" ? "No match" : p}: {stats[p]} ({Math.round((stats[p] * 100) / total)}%)
        </Badge>
      ))}
      <span className="text-xs text-muted-foreground self-center">{total} searches, last 7 days</span>
    </div>
  );
}


function TestLookup() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 3) { setRes(null); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await api.get("/address/autocomplete", { params: { q } }); setRes(data); } catch { setRes({ results: [], source: "error" }); } finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <Label className="text-xs">Try a lookup</Label>
      <div className="relative mt-1">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. 45 Sharp St Cooma" data-testid="gnaf-test-input" className="pl-8" />
      </div>
      {busy && <p className="mt-1 text-xs text-muted-foreground">Searching…</p>}
      {res && !busy && (
        <div className="mt-2" data-testid="gnaf-test-results">
          <p className="text-xs text-muted-foreground">{res.results.length} result{res.results.length === 1 ? "" : "s"} · source: <b data-testid="gnaf-test-source">{res.source || "none"}</b></p>
          <ul className="mt-1 space-y-1 text-sm">
            {res.results.slice(0, 6).map((r) => <li key={r.id} className="rounded border bg-secondary/40 px-2 py-1"><MapPin className="mr-1 inline h-3 w-3 text-kilted-green" />{r.label}{r.lat != null && <span className="ml-1 text-xs text-muted-foreground">({Number(r.lat).toFixed(5)}, {Number(r.lng).toFixed(5)})</span>}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AddressesTab() {
  const [s, setS] = useState(null);
  const [sample, setSample] = useState(null);
  const [url, setUrl] = useState("");
  const [path, setPath] = useState("");
  const [postcodes, setPostcodes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/gnaf/status").then((r) => { setS(r.data); setPostcodes((p) => p || r.data.postcode_spec || r.data.default_postcodes); }).catch(() => setS({ status: "never", providers: { nominatim: true } }));
    api.get("/admin/gnaf/sample").then((r) => setSample(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (s?.status !== "running") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [s?.status, load]);

  const startImport = async () => {
    setBusy(true);
    try {
      await api.post("/admin/gnaf/import", { url: url || null, path: path || null, postcodes });
      toast.success("G-NAF import started — progress shows below");
      setTimeout(load, 800);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start the import"); } finally { setBusy(false); }
  };
  const [checking, setChecking] = useState(false);
  const toggleAuto = async (v) => {
    try { const { data } = await api.put("/admin/gnaf/auto", { enabled: v }); setS((p) => ({ ...p, auto: { ...p.auto, ...data } })); toast.success(v ? "Automatic G-NAF updates on — checked every Sunday 2am" : "Automatic updates off"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const checkNow = async (startImport) => {
    setChecking(true);
    try {
      const { data } = await api.post(`/admin/gnaf/check${startImport ? "?start_import=true" : ""}`);
      if (data.error) toast.error(`Couldn't reach data.gov.au: ${data.error}`);
      else if (data.import_started) toast.success(`Importing ${data.latest.release} now`);
      else if (data.new_release_available) toast.info(`${data.latest.release} is available — you have ${data.imported_release || "nothing"} loaded`);
      else toast.success(`You're on the latest release (${data.latest.release})`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setChecking(false); }
  };
  const toggleNominatim = async (v) => {
    try { const { data } = await api.put("/admin/address-providers", { nominatim: v }); setS((p) => ({ ...p, providers: data })); toast.success(v ? "OpenStreetMap fallback on" : "OpenStreetMap fallback off — G-NAF, Google, then AddressFinder"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!s) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-primary" />;
  const loaded = !!s.count;
  return (
    <div className="space-y-6" data-testid="addresses-tab">
      <Card data-testid="gnaf-status-card">
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg"><Database className="h-5 w-5 text-kilted-gold" /> G-NAF address database</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">Australia's official geocoded address file, kept locally for the Monaro/ACT region. Searched first, instantly and free — no per-lookup cost.</p>
          </div>
          {loaded ? <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid="gnaf-badge-loaded">{Number(s.count).toLocaleString()} addresses</Badge>
            : <Badge variant="outline" className="border-amber-400 text-amber-700" data-testid="gnaf-badge-empty">Not imported yet</Badge>}
        </CardHeader>
        <CardContent className="space-y-4">
          {loaded && (
            <div className="grid gap-2 text-sm sm:grid-cols-3" data-testid="gnaf-meta">
              <div><span className="text-muted-foreground">Release</span><div className="font-medium">{s.release || "—"}</div></div>
              <div><span className="text-muted-foreground">Imported</span><div className="font-medium">{fmt(s.imported_at)}</div></div>
              <div><span className="text-muted-foreground">Postcodes</span><div className="font-medium">{s.postcode_spec || s.default_postcodes}</div></div>
            </div>
          )}
          {s.status === "running" && <ImportProgress s={s} />}
          {(s.status === "error" || s.status === "interrupted") && <p className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="gnaf-error">Last import {s.status === "interrupted" ? "was interrupted" : "failed"}: {s.error}</p>}
          {sample?.towns?.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="gnaf-towns">
              {sample.towns.map((t) => <Badge key={t.town} variant="secondary" className="font-normal">{t.town} · {t.count.toLocaleString()}</Badge>)}
            </div>
          )}
          <div className="rounded-md border border-kilted-green/30 bg-kilted-green/5 p-3" data-testid="gnaf-auto-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">Automatic quarterly updates</p>
                <p className="text-xs text-muted-foreground">Every Sunday 2am we ask data.gov.au for a newer G-NAF release and import it automatically.</p>
              </div>
              <span className="flex items-center gap-2 text-xs"><Switch checked={!!s.auto?.enabled} onCheckedChange={toggleAuto} data-testid="gnaf-auto-toggle" /> {s.auto?.enabled ? "On" : "Off"}</span>
            </div>
            {s.auto?.last_check && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="gnaf-auto-last-check">
                Last checked {fmt(s.auto.last_check.checked_at)} ({s.auto.last_check.trigger}) — {s.auto.last_check.error ? <span className="text-red-700">error: {s.auto.last_check.error}</span>
                  : s.auto.last_check.new_release_available ? <span className="font-medium text-amber-700">{s.auto.last_check.latest?.release} available{s.auto.last_check.latest?.size_mb ? ` (${(s.auto.last_check.latest.size_mb / 1024).toFixed(1)} GB)` : ""}{s.auto.last_check.import_started ? " — import started" : ""}</span>
                  : <span className="text-emerald-700">up to date ({s.auto.last_check.latest?.release})</span>}
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => checkNow(false)} disabled={checking} data-testid="gnaf-check-now" className="h-8 gap-1.5">{checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Check for updates</Button>
              {s.auto?.last_check?.new_release_available && !s.auto.last_check.import_started && s.status !== "running" && (
                <Button size="sm" onClick={() => checkNow(true)} disabled={checking} data-testid="gnaf-import-latest" className="h-8 gap-1.5 bg-kilted-green hover:bg-kilted-greenLight"><Download className="h-3.5 w-3.5" /> Import {s.auto.last_check.latest?.release} now</Button>
              )}
            </div>
          </div>
          <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <p className="text-sm font-medium">{loaded ? "Manual re-import" : "Import G-NAF manually"}</p>
              <p className="text-xs text-muted-foreground">Open <a href={DATA_GOV} target="_blank" rel="noreferrer" className="text-kilted-green underline">data.gov.au → G-NAF</a>, copy the direct link to the <b>GDA2020 PSV .zip</b> (≈1.5 GB) and paste it here. The server downloads it, keeps only your postcodes (NSW + ACT), and swaps the new data in when finished — lookups keep working meanwhile.</p>
            </div>
            <div className="sm:col-span-2"><Label className="text-xs">Direct download link (.zip)</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://data.gov.au/data/dataset/…/download/g-naf_…_gda2020_psv_….zip" data-testid="gnaf-url-input" className="mt-1" /></div>
            <div><Label className="text-xs">…or a file already on the server</Label><Input value={path} onChange={(e) => setPath(e.target.value)} placeholder="/app/backend/data/gnaf.zip" data-testid="gnaf-path-input" className="mt-1" /></div>
            <div><Label className="text-xs">Postcodes to keep</Label><Input value={postcodes} onChange={(e) => setPostcodes(e.target.value)} placeholder={s.default_postcodes} data-testid="gnaf-postcodes-input" className="mt-1" /></div>
            <div className="sm:col-span-2">
              <Button onClick={startImport} disabled={busy || s.running || s.status === "running" || (!url && !path)} data-testid="gnaf-import-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
                {busy || s.running || s.status === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {loaded ? "Re-import G-NAF" : "Import G-NAF"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="address-providers-card">
        <CardHeader><CardTitle className="text-lg">Lookup order & fallbacks</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            <li><b>G-NAF</b> (local database) — {loaded ? "active" : <span className="text-amber-700">inactive until imported</span>}</li>
            <li><b>Google Places</b> (paid, per lookup) — {s.google_configured ? "configured; also powers driving-distance pricing" : <span className="text-amber-700">not configured</span>}</li>
            <li><b>AddressFinder</b> (paid, per lookup) — {s.addressfinder_configured ? "configured" : <span className="text-amber-700">not configured</span>}</li>
            <li><span className="inline-flex flex-wrap items-center gap-3"><span><b>OpenStreetMap / Nominatim</b> — last resort, under review</span>
              <span className="flex items-center gap-2 text-xs"><Switch checked={!!s.providers?.nominatim} onCheckedChange={toggleNominatim} data-testid="nominatim-toggle" /> {s.providers?.nominatim ? "On" : "Off"}</span></span></li>
          </ol>
          <p className="text-xs text-muted-foreground">Each lookup is credited to the first provider that returns results. A weekly breakdown goes out in the Monday insights email so you can judge whether Nominatim is still earning its keep.</p>
          <LookupStats stats={s.lookups_7d} />
          <TestLookup />
        </CardContent>
      </Card>
    </div>
  );
}
